import {
  FillOperationParams,
  GridOptions,
  ISetFilterParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';

import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { TableConstants } from '@shared/constants/table.constants';
import { isValidDateFormat } from '@shared/utils/is-valid-date-format';
import { Utils } from '@shared/utils/utils';

import { AdjustmentsToPrepaidRow } from '../adjustments-to-prepaid.model';

export const DEFAULT_ADJUSTMENTS_TO_PREPAID_GRID_CONFIG: GridOptions<AdjustmentsToPrepaidRow> = {
  ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
  defaultColDef: {
    ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
    filter: true,
    filterParams: {
      showTooltips: true,
    } as ISetFilterParams,
    menuTabs: ['filterMenuTab'],
    minWidth: 100,
    resizable: true,
    sortable: true,
    suppressMenu: false,
    tooltipComponent: AgRawTooltip,
    tooltipValueGetter: (params) => params.valueFormatted || '',
    valueFormatter: (params: ValueFormatterParams) => params.value || ZERO_HYPHEN,
  },
  enableCellChangeFlash: true,
  enableRangeSelection: true,
  fillHandleDirection: 'y',
  fillOperation: (params: FillOperationParams) => {
    const columnId = params.column.getColId();

    if (columnId === 'invoice_id') {
      return params.currentCellValue;
    }

    if (
      columnId === 'accrual_period' ||
      columnId === 'payment_date' ||
      columnId === 'invoice_date'
    ) {
      return params.values[0];
    }

    return false;
  },
  getRowId: ({ data }) => data.id,
  headerHeight: 48,
  noRowsOverlayComponent: TableConstants.NO_ROWS_MESSAGE,
  // Custom sorting function that runs after all other sorting operations
  // Ensures rows with isNewAdjustment=true are always at the top of the grid
  postSortRows: (params) => {
    const { nodes } = params;

    // Sort the nodes array in place
    nodes.sort((a, b) => {
      // Safely access the isNewAdjustment property from row data
      const aIsNew = a.data?.isNewAdjustment;
      const bIsNew = b.data?.isNewAdjustment;

      // If a is new and b is not, a should come first (return -1)
      if (aIsNew && !bIsNew) return -1;

      // If b is new and a is not, b should come first (return 1)
      if (!aIsNew && bIsNew) return 1;

      // If both are new or both are not new, maintain their relative order (return 0)
      return 0;
    });
  },
  processCellForClipboard: (params) => params.value,
  processCellFromClipboard: (params) => {
    const colId = params.column.getColId();
    const numberColumns = ['long_term_amount', 'short_term_amount', 'invoice_total'];
    const dateColumns = ['invoice_date', 'payment_date', 'accrual_period'];

    if (numberColumns.includes(colId)) {
      let value = params.value;

      if (typeof value !== 'string') return value;

      // Remove currency symbols and spaces
      value = value.replace(/[^0-9.,-]/g, '');

      // Remove thousands separators (commas)
      value = value.replace(/,/g, '');

      // Now check if it's a valid number
      if (value === '' || isNaN(Number(value))) {
        return null; // Ignore invalid numbers (like UUIDs)
      }

      return Utils.castStringToNumber(value);
    }

    if (dateColumns.includes(colId)) {
      const value = params.value;

      if (typeof value !== 'string') return value;

      return isValidDateFormat(value) ? value : undefined;
    }

    return params.value;
  },
  rowHeight: 40,
  rowSelection: 'multiple',
  stopEditingWhenCellsLoseFocus: true,
  suppressAggFuncInHeader: true,
  suppressMenuHide: true,
  suppressRowClickSelection: true,
  suppressRowTransform: true,
  tooltipShowDelay: 500,
} satisfies GridOptions<AdjustmentsToPrepaidRow>;
